// Main Garden Viewer - coordinates scene and data loading

import { api, auth } from '@/api'
import { GardenScene, PlantRenderer, BuildingRenderer, GroundRenderer, SchemeRenderer, elevationService, modelLoader, type ViewMode, type LayerName, type LayerVisibility } from '@/scene'
import { debug } from '@/utils/debug'
import { t } from '@/i18n'
import type { Garden, Product, ProductFile, Layer } from '@/types'

export interface ViewerOptions {
  container: HTMLElement
  onStatusChange?: (status: string) => void
  onGardenLoaded?: (garden: Garden) => void
  onError?: (error: Error) => void
}

export class GardenViewer {
  private container: HTMLElement
  private scene: GardenScene | null = null
  private plantRenderer: PlantRenderer | null = null
  private buildingRenderer: BuildingRenderer | null = null
  private groundRenderer: GroundRenderer | null = null
  private schemeRenderer: SchemeRenderer | null = null

  private currentGarden: Garden | null = null
  private modelUrls: Map<string, ProductFile[]> = new Map()
  private textureUrls: Map<string, ProductFile[]> = new Map()  // For ground textures
  private products: Map<string, Product> = new Map()  // Product details (code, name, etc.)
  private categoryLayerMap: Map<string, Layer> = new Map()  // Category ID -> Layer mapping
  private isInitialized: boolean = false
  private initPromise: Promise<void> | null = null

  private onStatusChange?: (status: string) => void
  private onGardenLoaded?: (garden: Garden) => void
  private onError?: (error: Error) => void

  constructor(options: ViewerOptions) {
    this.container = options.container
    this.onStatusChange = options.onStatusChange
    this.onGardenLoaded = options.onGardenLoaded
    this.onError = options.onError
    debug.log('Viewer', 'GardenViewer created')
  }

  /**
   * Initialize the 3D scene (returns promise that resolves when ready)
   */
  async initialize(): Promise<void> {
    if (this.initPromise) {
      debug.log('Viewer', 'Already initializing, waiting...')
      return this.initPromise
    }

    this.initPromise = (async () => {
      try {
        this.setStatus(t('status.initializing'))
        debug.log('Scene', 'Creating GardenScene...')

        // Create scene first, then create renderers after assignment completes
        this.scene = new GardenScene({
          container: this.container
        })

        debug.success('Scene', 'GardenScene created')
        debug.log('Scene', 'Creating renderers...')

        // Now this.scene is definitely assigned
        this.plantRenderer = new PlantRenderer(this.scene)
        this.buildingRenderer = new BuildingRenderer(this.scene)
        this.groundRenderer = new GroundRenderer(this.scene)
        this.schemeRenderer = new SchemeRenderer(this.scene)

        this.isInitialized = true
        debug.success('Viewer', 'All renderers initialized')
        this.setStatus(t('status.ready'))
      } catch (error) {
        debug.error('Scene', 'Failed to create scene', error)
        throw error
      }
    })()

    return this.initPromise
  }

  /**
   * Wait for initialization to complete
   */
  async waitForInit(): Promise<void> {
    if (this.isInitialized) return
    if (this.initPromise) {
      await this.initPromise
    } else {
      throw new Error('Viewer not initialized. Call initialize() first.')
    }
  }

  /**
   * Load and render a garden by ID
   */
  async loadGarden(gardenId: string): Promise<void> {
    debug.log('Garden', `Loading garden: ${gardenId}`)

    // Wait for initialization
    await this.waitForInit()
    debug.log('Garden', 'Viewer initialized, proceeding with load')

    if (!this.scene || !this.groundRenderer || !this.plantRenderer || !this.buildingRenderer) {
      const error = new Error('Scene or renderers not initialized')
      debug.error('Garden', 'Missing components', {
        scene: !!this.scene,
        groundRenderer: !!this.groundRenderer,
        plantRenderer: !!this.plantRenderer,
        buildingRenderer: !!this.buildingRenderer
      })
      this.onError?.(error)
      return
    }

    try {
      // Fetch garden metadata
      this.setStatus(t('status.loadingGarden'))
      debug.log('API', `Fetching garden ${gardenId}...`)

      const authHeader = auth.getState()
      debug.log('API', 'Auth state', { isAuthenticated: authHeader.isAuthenticated })

      const garden = await api.getGarden(gardenId)

      // Fetch category layer map if not already loaded
      if (this.categoryLayerMap.size === 0) {
        this.setStatus(t('status.loadingCategories'))
        this.categoryLayerMap = await api.getCategoryLayerMap()
        debug.log('Garden', `Category layer map loaded: ${this.categoryLayerMap.size} categories`)
      }

      // Fetch all garden objects with products and textures in one API call
      this.setStatus(t('status.loadingAssets'))
      const assets = await api.getGardenWithAssets(gardenId, {
        textureKinds: ['glb', 'top'],
        categoryLayerMap: this.categoryLayerMap
      })

      // Populate garden with objects
      garden.plants = assets.plants
      garden.buildings = assets.buildings
      garden.groundShapes = assets.groundShapes

      // Store asset maps
      this.products = assets.products
      this.modelUrls = assets.modelUrls
      this.textureUrls = assets.textureUrls

      this.currentGarden = garden

      debug.success('API', `Garden loaded: "${garden.name}"`, {
        plants: garden.plants.length,
        buildings: garden.buildings.length,
        groundShapes: garden.groundShapes.length
      })

      // Collect all GLB URLs for parallel preloading
      const glbUrls: string[] = []
      for (const files of this.modelUrls.values()) {
        const glbFile = files.find(f => f.kind === 'glb')
        if (glbFile?.fileUrl) {
          glbUrls.push(glbFile.fileUrl)
        }
      }

      // Preload all models in parallel (if any)
      if (glbUrls.length > 0) {
        this.setStatus(t('status.preloadingModels'))
        debug.log('Preload', `Preloading ${glbUrls.length} GLB models in parallel...`)
        await modelLoader.preloadMany(glbUrls)
        debug.success('Preload', `All ${glbUrls.length} models preloaded`)
      }

      // Focus camera on garden center BEFORE rendering
      // This prevents camera "snap" when user can see scene through semi-transparent overlay
      this.focusOnGarden(garden)

      // Set sun direction based on garden rotation
      this.scene!.setSunDirection(garden.rotation)

      // Render all objects
      await this.renderGarden(garden)

      // Sync plant renderer with current view mode (for &camera=3D startup)
      this.plantRenderer?.setViewMode(this.scene!.getViewMode())

      this.setStatus(t('status.gardenLoaded'))
      debug.success('Garden', 'Garden fully rendered')
      this.onGardenLoaded?.(garden)

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      debug.error('Garden', 'Failed to load garden', err)
      this.setStatus(`${t('error.prefix')} ${err.message}`)
      this.onError?.(err)
    }
  }

  /**
   * Render all garden objects
   */
  private async renderGarden(garden: Garden): Promise<void> {
    debug.group('Rendering garden')

    // Clear existing objects
    debug.log('Render', 'Clearing existing objects...')
    this.scene!.clearAllObjects()
    this.schemeRenderer!.dispose()

    // Render scheme image first (background layer)
    if (garden.schemeImage?.imageUrl) {
      this.setStatus(t('status.renderingScheme'))
      debug.log('Render', 'Rendering scheme background image...')
      await this.schemeRenderer!.load(garden.schemeImage)
    }

    // Render scheme outline if present
    if (garden.schemeOutline) {
      debug.log('Render', 'Rendering scheme outline...')
      this.schemeRenderer!.renderOutline(garden.schemeOutline)
    }

    // Render ground shapes (bottom layer)
    this.setStatus(t('status.renderingGround'))
    debug.log('Render', `Rendering ${garden.groundShapes.length} ground shapes...`)
    await this.groundRenderer!.renderShapes(garden.groundShapes, this.textureUrls)
    debug.success('Render', `Ground: ${this.groundRenderer!.count} shapes rendered`)

    // Register ground shapes for elevation calculation (buildings and plants query this)
    elevationService.setGroundShapes(garden.groundShapes, this.products)

    // Render buildings
    this.setStatus(t('status.renderingBuildings'))
    debug.log('Render', `Rendering ${garden.buildings.length} buildings...`)
    await this.buildingRenderer!.renderBuildings(garden.buildings, this.modelUrls, this.products)
    debug.success('Render', `Buildings: ${this.buildingRenderer!.count} rendered`)

    // Register buildings for elevation calculation (plants query this)
    elevationService.setBuildings(garden.buildings, this.products)

    // Render plants (top layer)
    this.setStatus(t('status.renderingPlants'))
    debug.log('Render', `Rendering ${garden.plants.length} plants...`)
    await this.plantRenderer!.renderPlants(garden.plants, this.modelUrls, this.textureUrls, this.products)
    debug.success('Render', `Plants: ${this.plantRenderer!.count} rendered`)

    debug.groupEnd()
  }

  /**
   * Focus camera on the center of the garden
   */
  private focusOnGarden(garden: Garden): void {
    if (!this.scene) return

    // Calculate bounding box of all objects
    let minX = Infinity, maxX = -Infinity
    let minY = Infinity, maxY = -Infinity

    const allObjects = [...garden.plants, ...garden.buildings, ...garden.groundShapes]

    if (allObjects.length === 0) {
      debug.log('Camera', 'No objects, using default view')
      this.scene.focusOn(500, 500, 0, 1000, 1000)
      return
    }

    for (const obj of allObjects) {
      minX = Math.min(minX, obj.x)
      maxX = Math.max(maxX, obj.x + obj.width)
      minY = Math.min(minY, obj.y)
      maxY = Math.max(maxY, obj.y + obj.height)
    }

    // Center and size of all objects
    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2
    const boundsWidth = maxX - minX
    const boundsHeight = maxY - minY

    debug.log('Camera', `Focusing on center: (${centerX}, ${centerY}), bounds: ${boundsWidth}x${boundsHeight}`)
    this.scene.focusOn(centerX, centerY, 0, boundsWidth, boundsHeight)
  }

  /**
   * Get current garden data
   */
  getGarden(): Garden | null {
    return this.currentGarden
  }

  /**
   * Get object counts for UI display
   */
  getStats(): { plants: number; buildings: number; shapes: number; hasScheme: boolean } {
    return {
      plants: this.plantRenderer?.count || 0,
      buildings: this.buildingRenderer?.count || 0,
      shapes: this.groundRenderer?.count || 0,
      hasScheme: this.schemeRenderer?.isLoaded() || false
    }
  }

  /**
   * Set scheme layer visibility
   */
  setSchemeVisible(visible: boolean): void {
    this.schemeRenderer?.setVisible(visible)
  }

  /**
   * Get scheme layer visibility
   */
  isSchemeVisible(): boolean {
    return this.schemeRenderer?.isVisible() ?? false
  }

  /**
   * Set view mode (2D or 3D)
   */
  setViewMode(mode: ViewMode): void {
    this.scene?.setViewMode(mode)
    this.plantRenderer?.setViewMode(mode)
  }

  /**
   * Get current view mode
   */
  getViewMode(): ViewMode {
    return this.scene?.getViewMode() ?? '2d'
  }

  /**
   * Enable or disable walk mode (first-person view)
   */
  setWalkMode(enabled: boolean): void {
    this.scene?.setWalkMode(enabled)
  }

  /**
   * Check if walk mode is active
   */
  isWalkMode(): boolean {
    return this.scene?.isWalkMode() ?? false
  }

  /**
   * Set callback for walk mode changes
   */
  setOnWalkModeChange(callback: (enabled: boolean) => void): void {
    this.scene?.setOnWalkModeChange(callback)
  }

  /**
   * Set visibility of a specific layer
   */
  setLayerVisible(layer: LayerName, visible: boolean): void {
    this.scene?.setLayerVisible(layer, visible)
    // Metadata layer also controls SchemeRenderer
    if (layer === 'metadata') {
      this.schemeRenderer?.setVisible(visible)
    }
  }

  /**
   * Toggle visibility of a specific layer
   * @returns The new visibility state
   */
  toggleLayer(layer: LayerName): boolean {
    const newState = this.scene?.toggleLayer(layer) ?? true
    // Metadata layer also controls SchemeRenderer
    if (layer === 'metadata') {
      this.schemeRenderer?.setVisible(newState)
    }
    return newState
  }

  /**
   * Get current layer visibility state
   */
  getLayerVisibility(): LayerVisibility {
    return this.scene?.getLayerVisibility() ?? {
      plants: true,
      buildings: true,
      ground: true,
      metadata: true
    }
  }

  /**
   * Check if a specific layer is visible
   */
  isLayerVisible(layer: LayerName): boolean {
    return this.scene?.isLayerVisible(layer) ?? true
  }

  /**
   * Set callback for layer visibility changes
   */
  setOnLayerVisibilityChange(callback: (layer: LayerName, visible: boolean) => void): void {
    this.scene?.setOnLayerVisibilityChange(callback)
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    debug.log('Viewer', 'Disposing viewer...')
    this.plantRenderer?.clear()
    this.buildingRenderer?.clear()
    this.groundRenderer?.clear()
    this.schemeRenderer?.dispose()
    this.scene?.dispose()
    this.scene = null
    this.isInitialized = false
    this.initPromise = null
  }

  private setStatus(status: string): void {
    debug.log('Status', status)
    this.onStatusChange?.(status)
  }
}
